import { Component, Host, h, Prop, Watch, State, Element } from "@stencil/core";

let iconCounter = 0;

// Shared cache for all instances to store fetched SVG icons
const iconCache = new Map<string, Promise<string>>();

@Component({
  tag: "stzh-icon",
  styleUrl: "stzh-icon.scss",
  shadow: true,
})
export class StzhIcon {
  /**
   * Icon which should be displayed
   * See the [icon overview](stzh-components/assets/svgsprites/symbol/sprite.symbol.html) for a list of all available icons.
   */
  @Prop() name: string = "";

  /**
   * Hidden icon title for screenreader
   * (usually not needed when e.g. inside links or buttons that have text)
   */
  @Prop({ attribute: "a11y-title" }) a11yTitle: string = "";

  @Element() element: HTMLStzhIconElement;

  @State() svgHtml: string = "";
  @State() iconDisplay: string = "none";

  @Watch("name")
  async nameWatcher(name: string) {
    this.iconDisplay = "none";

    if (name) {
      if (iconCache.has(name)) {
        this.svgHtml = await iconCache.get(name)!;
        this.updateAccessibilityAttributes();
        this.showIcon();
      } else {
        const fetchSvgPromise = fetch(`${window.stzhComponents.pathMedia}/icons/mono/${this.name}.svg`)
          .then(response => {
            if (response.status === 200) {
              return response.text();
            }
            throw new Error("Failed to fetch icon");
          })
          .then(svgString => {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
            const svgElement = svgDoc.documentElement as unknown as SVGElement;
            svgElement.classList.add("stzh-icon__svg");

            return svgElement.outerHTML;
          });

        iconCache.set(name, fetchSvgPromise);

        this.svgHtml = await fetchSvgPromise;
        this.updateAccessibilityAttributes();
        this.showIcon();
      }
    } else {
      this.svgElement = null;
      this.svgHtml = "";
    }
  }

  @Watch("a11yTitle")
  a11yTitleWatcher(a11yTitle: string) {
    if (!this.svgElement) return;

    this.svgElement.setAttribute("aria-hidden", a11yTitle ? "false" : "true");
    this.svgElement.setAttribute("focusable", a11yTitle ? "true" : "false");
    this.svgElement.setAttribute("aria-labelledby", a11yTitle ? `${this.iconId}-title` : null);

    let title = this.svgElement.querySelector("title");

    if (!title) {
      title = document.createElement("title");
      title.id = `${this.iconId}-title`;
      this.svgElement.appendChild(title);
    }

    title.innerHTML = a11yTitle;
    this.svgHtml = this.svgElement.outerHTML;
  }

  private svgElement: SVGElement | null;
  private iconId: string;

  async componentWillLoad() {
    this.iconId = `stzh-icon-${iconCounter++}`;
    await this.nameWatcher(this.name);
  }

  private updateAccessibilityAttributes() {
    this.a11yTitleWatcher(this.a11yTitle);
  }

  private showIcon() {
    // for some obscure reason we need to delay showing the icon in safari,
    // otherwise the icon doesn't show up randomly
    // TODO: sometimes the icon still randomely disappears when clicked on page (check on newer safari if problems still occur)
    window.setTimeout(() => {
      this.iconDisplay = "contents";
    }, 0);
  }

  render() {
    return (
      <Host>
        <div class="stzh-icon" style={{ display: this.iconDisplay }} innerHTML={this.svgHtml}></div>
      </Host>
    );
  }
}
